import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
} from "@tanstack/react-router";
import { AuthProvider } from "@/lib/auth-context";
import { publicSettingsQueryOptions } from "@/lib/settings.queries";
import { DEFAULT_PUBLIC_SETTINGS, pageTitle, type PublicSettings } from "@/lib/settings.functions";
import { DirectionProvider } from "@/lib/direction";
import { CartProvider } from "@/lib/cart";
import { TopBar } from "@/components/site/TopBar";
import { Footer } from "@/components/site/Footer";
import { CartSheet } from "@/components/site/CartSheet";
import { Analytics } from "@/components/site/Analytics";
import { Toaster } from "@/components/ui/sonner";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong. Try again or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

/**
 * Site-wide head defaults, driven by Settings > SEO. Child routes that set
 * their own title / description / og:image win (HeadContent dedupes by name
 * and property, deepest match last). Without a share image the card type is
 * "summary": "summary_large_image" with no image renders as a broken card.
 */
function rootMeta(settings: PublicSettings) {
  const seo = settings.seo;
  const title = pageTitle(null, seo);
  const meta: Array<Record<string, string>> = [
    { title },
    { name: "description", content: seo.defaultDescription },
    { property: "og:title", content: title },
    { property: "og:description", content: seo.defaultDescription },
    { property: "og:type", content: "website" },
    { name: "twitter:title", content: title },
    { name: "twitter:card", content: seo.defaultShareImage ? "summary_large_image" : "summary" },
  ];
  if (seo.defaultShareImage) {
    const img = seo.defaultShareImage.startsWith("/")
      ? (typeof window !== "undefined" ? window.location.origin : "") + seo.defaultShareImage
      : seo.defaultShareImage;
    meta.push({ property: "og:image", content: img }, { name: "twitter:image", content: img });
  }
  if (seo.searchConsoleTag) {
    meta.push({ name: "google-site-verification", content: seo.searchConsoleTag });
  }
  return meta;
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // The public settings feed the head meta below. The query never rejects
  // (it falls back to the shipped defaults), so this cannot block a page on
  // an API outage; after the first load it is served from the 5-minute cache.
  loader: async ({ context }) => {
    try {
      const settings = await context.queryClient.ensureQueryData(publicSettingsQueryOptions());
      return { settings };
    } catch {
      return { settings: DEFAULT_PUBLIC_SETTINGS };
    }
  },
  head: ({ loaderData }) => ({
    meta: rootMeta(loaderData?.settings ?? DEFAULT_PUBLIC_SETTINGS),
  }),
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <HeadContent />
      <AuthProvider>
        <DirectionProvider>
          <CartProvider>
            <div className="flex min-h-screen flex-col bg-background text-foreground">
              <TopBar />
              <main className="flex-1">
                <Outlet />
              </main>
              <Footer />
              <CartSheet />
              <Analytics />
              <Toaster richColors position="top-right" />
            </div>
          </CartProvider>
        </DirectionProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
